import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, Box, CircularProgress, List, ListItem, ListItemText, Grid, Paper, Avatar } from '@mui/material';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';
import PersonIcon from '@mui/icons-material/Person';
import { Language, LocationOn, Business } from '@mui/icons-material';


const API_URL = process.env.REACT_APP_API_URL;

const OrganizationTabDetails = ({ userId, onLogout }) => {

  useEffect(() => {
    window.scrollTo(0, 0);  // Scroll to top when this page is loaded
  }, []);

  const { orgId } = useParams();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      try {
        const response = await axios.post(`${API_URL}/organization/`, { org_id: orgId });
        setOrganization(response.data.organization);
        setUsers(response.data.users);
        setLoading(false);
      } catch (err) {
        setError('Error fetching data');
        setLoading(false);
      }
    };

    fetchOrganizationDetails();
  }, [orgId]);

  if (loading) {
    return (
      <Box textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />

      <Typography
        variant="h3"
        component="h1"
        align="center"
        fontSize={{ xs: '2rem', sm: '2.5rem', md: '3rem' }}
        sx={{
          backgroundImage: 'linear-gradient(45deg, #ff00ff, #00bfff)',
          mt: 10,
          letterSpacing: 7,
          WebkitBackgroundClip: 'text',  // This makes the gradient clip to the text
          color: 'transparent',  // Makes the text itself transparent to show the gradient
          // fontWeight: 'bold',  // Optional: you can make the text bold
        }}
        gutterBottom
      >
        Organization View
      </Typography>

      <Grid container justifyContent="center">
        <Box sx={{
          padding: { xs: "2rem", sm: "3rem" }, // Responsive padding
          backgroundColor: "#2222", // Darker background for modern look
          color: "#fff",
          borderRadius: "16px",
          height: "100%",

          boxSizing: "border-box",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
          },
        }}>

          <Container sx={{ mt: 3, textAlign: 'center' }}>

            <Typography variant="h4" gutterBottom sx={{ letterSpacing: 2, }}>
              {organization.name}
            </Typography>

            <img
              src={`${API_URL}${organization.logo}`}
              alt={`${organization.name} logo`}
              style={{
                maxWidth: '150px',
                margin: '20px auto',  // Center image horizontally
                borderRadius: '50%'    // Optional: round logo if you prefer
              }}
            />

            {/* Main Content */}
            <Paper sx={{
              padding: 3,
              backgroundColor: '#2222',  // Darker card background
              borderRadius: 2,
              boxShadow: 6,
              marginBottom: 3,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: 10, // Increased shadow on hover for more effect
                transform: 'scale(1.02)', // Slight zoom effect on hover
              },
            }}>
              <Typography
                variant="h6"
                paragraph
                sx={{
                  lineHeight: 1.7,
                  color: '#e0e0e0',
                  fontWeight: 400,
                  letterSpacing: 2,
                  fontSize: {
                    xs: '1rem',  // Font size for extra small devices (mobile)
                    sm: '1.2rem', // Font size for small devices (tablet)
                    md: '1.4rem', // Font size for medium devices (small laptops)
                    lg: '1.4rem', // Font size for large devices (desktops)
                    xl: '1.2rem', // Font size for extra large devices (large desktops)
                  },
                  padding: {
                    xs: '10px',  // Padding for extra small screens
                    sm: '20px',  // Padding for small screens
                    md: '20px',  // Padding for medium screens
                    lg: '15px',  // Padding for large screens
                  },
                  marginBottom: {
                    xs: '20px',  // Bottom margin for small screens
                    sm: '30px',  // Bottom margin for medium screens
                    md: '15px',  // Bottom margin for larger screens
                  },
                }}
              >
                {organization.about}
              </Typography>

            </Paper>

            <Box
              sx={{
                maxWidth: '1200px',
                margin: 'auto',
                padding: 3,
                marginTop: 4,
                backgroundColor: '#121212',
                color: '#e0e0e0',
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              <Grid container spacing={3}>
                {/* Industry Card */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                  <Paper
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: 2,
                      backgroundColor: '#333',
                      borderRadius: 2,
                      boxShadow: 2,
                      transition: 'all 0.3s ease-in-out',
                      height: '100%',
                      width: '100%',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Business sx={{ fontSize: 40, color: '#1976d2', marginRight: 2 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, letterSpacing: 2 }}>
                      <strong>Industry:</strong> {organization.industry_type}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Website Card */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                  <Paper
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: 2,
                      backgroundColor: '#333',
                      borderRadius: 2,
                      boxShadow: 2,
                      transition: 'all 0.3s ease-in-out',
                      height: '100%',
                      width: '100%',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Language sx={{ fontSize: 40, color: '#1976d2', marginRight: 2 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, letterSpacing: 2 }}>
                      <strong>Website:</strong>{' '}
                      <Box
                        onClick={() => window.open(organization.website, '_blank')}
                        sx={{
                          color: '#007BFF',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          fontWeight: 600,
                          letterSpacing: 2,
                          transition: 'color 0.3s ease',
                          '&:hover': {
                            color: '#0056b3',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Click to Visit
                      </Box>
                    </Typography>
                  </Paper>
                </Grid>

                {/* Location Card */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                  <Paper
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      padding: 2,
                      backgroundColor: '#333',
                      borderRadius: 2,
                      boxShadow: 2,
                      transition: 'all 0.3s ease-in-out',
                      height: '100%',
                      width: '100%',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <LocationOn sx={{ fontSize: 40, color: '#1976d2', marginRight: 2 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, letterSpacing: 2 }}>
                      <strong>Location:</strong>{' '}
                      <Box
                        onClick={() => window.open(organization.location_google_maps_link, '_blank')}
                        sx={{
                          color: '#1E90FF',
                          cursor: 'pointer',
                          textDecoration: 'none',
                          fontWeight: 600,
                          letterSpacing: 2,
                          transition: 'color 0.3s ease',
                          '&:hover': {
                            color: '#0d6efd',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Click to View on Map
                      </Box>
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#fff', mt: 5, letterSpacing: 3, }}>
              Organization Users:
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {users.map(user => (

                <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
                  <Link to={`/request-user-tab-details/${user.id}`} style={{ textDecoration: "none" }}>
                    <Paper sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: 3,
                      backgroundColor: '#333',
                      borderRadius: 2,
                      boxShadow: 2,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'scale(1.05)',
                      },
                      cursor: 'pointer',
                    }}>
                      {/* Avatar + Name */}
                      <Avatar sx={{
                        width: 60,
                        height: 60,
                        marginBottom: 2,
                        backgroundColor: '#1976d2',  // Custom color for Avatar
                      }}>
                        <PersonIcon />
                      </Avatar>

                      {/* Name + Surname */}

                      <ListItemText
                        primary={
                          <>
                            <Typography variant="h7" sx={{ color: '#fff', fontWeight: 500, letterSpacing: 2, }}>
                              {`${user.name} ${user.surname}`}
                            </Typography>
                          </>
                        }
                      />

                    </Paper>
                  </Link>
                </Grid>

              ))}
            </Grid>
          </Container>
        </Box>
      </Grid>

      <Footer2 />
    </div>
  );
};

export default OrganizationTabDetails;
